import { isGenericCompanyDetails } from '@/modules/company/utils/isGenericCompanyDetails'
import {
  useCart,
  useCompany,
  useKillerQuestions,
  useLocation,
} from '@/services'
import { areLoading } from '@/utils'
import { keys, omit, path, pluck } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { CheckoutStepKeys, checkoutStepIndexes, checkoutSteps } from './steps'
import { CartRecommendationProduct } from '@/types/recommendation'
import { CheckoutStep } from '@/modules/checkout/components/CheckoutBase'

const makeCheckoutSteps = ({
  poaSigned,
  killerQuestions,
  messages,
  tooltips,
  itemCount,
}: {
  poaSigned: boolean
  killerQuestions: any[]
  messages: {
    titles: Record<string, string>
    subtitles: Record<string, string>
  }
  tooltips: Record<string, React.ReactNode>
  isGenericCompany: boolean
  itemCount: number
}) => {
  const keysToOmit = []

  if (poaSigned) keysToOmit.push(CheckoutStepKeys.poa)

  if (poaSigned) keysToOmit.push(CheckoutStepKeys.companyDetails)

  if (!killerQuestions?.length)
    keysToOmit.push(CheckoutStepKeys.killerQuestions)

  if (itemCount !== 1) keysToOmit.push(CheckoutStepKeys.overview)

  return (keys(omit(keysToOmit, checkoutSteps)).map((key) => ({
    key,
    keyIndex: checkoutStepIndexes[key],
    Component: checkoutSteps[key],
    title: messages.titles[key],
    subtitle: messages.subtitles[key],
    tooltip: tooltips[key],
  })) as unknown) as CheckoutStep[]
}

const useMessages = () => {
  const { t } = useTranslation()

  return {
    titles: {
      payment: t('checkout.titles.payment'),
      killerQuestions: t('checkout.titles.killerQuestions'),
      poa: t('checkout.titles.poa'),
      overview: t('checkout.titles.overview'),
      success: t('checkout.titles.success'),
    },
    subtitles: {
      overview: t('checkout.subtitles.overview'),
      success: t('checkout.subtitles.success'),
    },
  }
}

const tooltips = {}

export const useCheckoutSteps = (data: CartRecommendationProduct[] = []) => {
  const messages = useMessages()
  const { locationId } = useParams()
  const locationResponse = useLocation(locationId || '', {
    enabled: !!locationId,
  })
  const companyResponse = useCompany(locationResponse.data?.companyId || '', {
    enabled: !!locationResponse.data?.companyId,
  })
  const { isLoading } = useCart()
  const categories = pluck('categoryId', data)
  const killerQuestionsResponse = useKillerQuestions(categories, {
    enabled: !!categories.length,
  })

  return {
    isLoading: areLoading([
      { isLoading: locationResponse.isLoading },
      { isLoading: killerQuestionsResponse.isLoading },
      { isLoading },
    ]),
    steps: makeCheckoutSteps({
      poaSigned:
        path(['data', 'company', 'poaSigned'], locationResponse) ?? false,
      killerQuestions: killerQuestionsResponse.data?.data,
      messages,
      tooltips,
      isGenericCompany: isGenericCompanyDetails(
        companyResponse.data?.name || '',
        companyResponse.data?.address,
      ),
      itemCount: data.length,
    }),
    companyId: locationResponse.data?.companyId,
    killerQuestions: killerQuestionsResponse.data?.data || [],
  }
}
